import { MouseEvent, useContext, useMemo, useState } from "react";
import { useQuery } from "react-query";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { RevenueOverview } from "@arelia-types/earnings";
import { RevenuePresenter } from "../RevenuePresenter";
import { getApiResponseMapper } from "../utils";
import { RevenueEditDialog } from "../RevenueEditDialog";
import { API_ROUTE, merchandiseRevenueColumnConfig } from "./constants";
import { MERCH_UPDATE_EVENT_NAME, PERFORMANCE_UPDATE_EVENT_NAME } from "../../../features/earnings/constants";
import WidgetMenuSelector from "@arelia-components/ui-atoms/WidgetMenuSelector";
import useEventListeners from "@arelia-hooks/useEventListeners";
import { http } from "src/api/http";
import { formatDate } from "@arelia-utils/helpers/dates";
import EditAciton from "@arelia-components/ui-atoms/EditAciton";
import { EarningsPeriod, EarningsPeriodDisplay, PeriodDisplay } from "../../../utils/constants/constants";
import getMenuItems from "@arelia-utils/helpers/getMenuItems";
import { Card, CardContent, CardHeader } from "@mui/material";
import AppContext from "src/AppContext";
import UnavailableContent from "@arelia-components/ui-atoms/UnavailableContent";
import { SubscriptionPlanName } from "@arelia-types/subscriptions";

export function MerchandiseContainer() {
  const [dialogOpened, setDialogOpened] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(EarningsPeriod.Year);
  const [selectedPeriodDisplay, setSelectedPerionDisplay] = useState<PeriodDisplay>(EarningsPeriodDisplay[EarningsPeriod.Year]);
  const [take, setTake] = useState(5);
  const { account } = useContext(AppContext);

  const { data: recordsResponse, isLoading: loadingRecords, isError: errorRecords, refetch: refreshRecords } = useQuery<RevenueOverview>({
    queryFn: () => http(`/api/earnings/merchandise?skip=0&take=${take}&period=${selectedPeriod}`),
    queryKey: ["merchandiseRevenueRecords", selectedPeriod, take]
  });

  const recordsData: RevenueOverview = useMemo(() => recordsResponse as RevenueOverview, [recordsResponse]);

  const handleDialogClose = () => {
    setDialogOpened(false);
  };

  const handleTotalsRefresh = () => {
    refreshRecords();

    const event = new CustomEvent(MERCH_UPDATE_EVENT_NAME);
    window.dispatchEvent(event);
  };

  useEventListeners([PERFORMANCE_UPDATE_EVENT_NAME], () => {
    handleTotalsRefresh();
  });

  const menuItems = getMenuItems(EarningsPeriodDisplay);

  const setPeriod = (period: EarningsPeriod) => {
    setSelectedPeriod(period);
    setSelectedPerionDisplay(EarningsPeriodDisplay[period]);
  }

  const renderPeriodSelector = () => (
    <WidgetMenuSelector<EarningsPeriod>
      onClick={setPeriod}
      selectedValue={selectedPeriod}
      labelText={selectedPeriodDisplay.menuLabelDisplay}
      menuItems={menuItems}
    />
  );

  const receiveAllData = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setTake(9999);
  };

  const startEdit = (event: MouseEvent) => {
    event.stopPropagation();
    setDialogOpened(true);
  };

  return account?.plan === SubscriptionPlanName.Starter
    ?
    <Card sx={{ height: "100%" }}>
      <CardHeader title="Merchandise Revenue" />
      <CardContent>
        <UnavailableContent message="To add/edit Merchandise Revenue upgrade to My Social" />
      </CardContent>
    </Card>
    :
    <>
      <WidgetCard
        widgetId="MERCHANDISE_REVENUE"
        title="merchandise revenue"
        loading={loadingRecords}
        error={errorRecords}
        footerText={recordsData && recordsData.updatedAt ? `Last updated: ${formatDate(recordsData.updatedAt, "P")}` : ''}
        headerComponent={renderPeriodSelector()}
        extraActions={<EditAciton editClicked={startEdit} />}
      >
        <RevenuePresenter
          data={recordsData}
          records={getApiResponseMapper(recordsData?.data)}
          columnConfig={merchandiseRevenueColumnConfig as any[]}
          selectedPeriod={selectedPeriodDisplay.timespanDisplay}
          receiveAllData={receiveAllData}
        ></RevenuePresenter>
      </WidgetCard>
      {dialogOpened && <RevenueEditDialog<EarningsPeriod>
        open={dialogOpened}
        data={recordsData}
        apiRoute={API_ROUTE}
        columnConfig={merchandiseRevenueColumnConfig}
        title={"Merchandise Revenue"}
        onClose={handleDialogClose}
        onTotalsRefresh={handleTotalsRefresh}
        headerComponent={renderPeriodSelector()}
        selectedPeriod={selectedPeriod}
        selectedPeriodDisplay={selectedPeriodDisplay.timespanDisplay}
      />}
    </>
  ;
}
